const TrackingEvents = {
  track: 'asyncTrackingEvent',
  queue: 'asyncTrackBacklog',
  scroll: 'scroll'
};
const abacusDataLayerName = 'abacus';

/** Must match inferno-spark `CLIENT_SESSION_STORAGE_KEY`. */
const CLIENT_SESSION_STORAGE_KEY = 'client_session_id';

let lastPageLocation;
let lastGtmPageLocation;
let scrollCollection = {};

const generateClientSessionId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const readClientSessionCookie = () => {
  const cookiePrefix = `${CLIENT_SESSION_STORAGE_KEY}=`;
  const cookies = document.cookie.split(';');
  for (const cookiePart of cookies) {
    const trimmed = cookiePart.trim();
    if (trimmed.startsWith(cookiePrefix)) {
      const value = decodeURIComponent(trimmed.slice(cookiePrefix.length));
      if (value.length > 0) {
        return value;
      }
    }
  }
  return undefined;
};

const writeClientSessionCookie = (sessionId) => {
  document.cookie = `${CLIENT_SESSION_STORAGE_KEY}=${encodeURIComponent(sessionId)};path=/;SameSite=Lax`;
};

/** Client analytics session id (session cookie). Same logic as inferno-spark clientSession.ts. */
const getClientSessionId = () => {
  const existing = readClientSessionCookie();
  if (existing) {
    return existing;
  }
  const sessionId = generateClientSessionId();
  writeClientSessionCookie(sessionId);
  return sessionId;
};

const withClientSessionOnPage = (page) => ({
  ...page,
  session: getClientSessionId()
});

const getScrollHeight = () => {
  return Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
  );
};

const getTrackingData = (data) => {
  const track = { data: {} };
  for (const property in data) {
    // eslint-disable-next-line no-prototype-builtins
    if (data.hasOwnProperty(property) && property !== 'track') {
      if (property === 'trackAbacus') {
        track.event = data[property];
      } else if (property === 'trackExternalId') {
        if (data[property]) {
          track.data.link_id = data[property];
        }
      } else {
        property === 'trackUrl' &&
          data[property]?.indexOf('http') === -1 &&
          (data[property] = document.location.origin + data[property]);
        property === 'trackTarget' &&
          data[property]?.indexOf('http') === -1 &&
          (data[property] = document.location.origin + data[property]);
        track.data[property.replace('track', '').toLowerCase()] = data[property];
      }
    }
  }
  return track;
};

const getSpecificPageTrackingData = () => {
  const data = {};
  for (const property in document.body.dataset) {
    if (property.match(/^track/gm)) {
      if (property === 'trackExternalId') {
        if (document.body.dataset[property]) {
          data.link_id = document.body.dataset[property];
        }
      } else {
        data[snakeize(camelize(property.replace('track', '')))] = document.body.dataset[property];
      }
    }
  }
  return data;
};

const camelize = (str) => {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
};

const snakeize = (str) => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

const addAbacusDataLayerToWindow = (api, defaultDataLayer) => {
  // Create property on window object named abacus
  window[abacusDataLayerName] =
    window[abacusDataLayerName] ||
    (function (u, dp = {}) {
      const rv = {};
      let _page = { page: {} };
      rv.dataBacklog = [];
      rv.queueOnly = false;
      rv.push = function (...args) {
        if (this.queueOnly) {
          this.dataBacklog.push({ detail: { trackAbacus: args[0].event } });
          return;
        }
        _page = args[0].page ? { page: withClientSessionOnPage(args[0].page) } : _page;
        const _data = {
          ..._page,
          ...dp,
          ...args[0],
          page: args[0].page
            ? withClientSessionOnPage(args[0].page)
            : { session: getClientSessionId() }
        };
        fetch(u, {
          method: 'POST',
          body: JSON.stringify(_data)
        }).then((response) => {
          // if (process.env.NODE_ENV != 'production') {
          //   console.log('Abacus: ', _data);
          //   console.log('Abacus: ', response);
          // }
        });
      };
      return rv;
    })(api, defaultDataLayer.value);
  if (process.env.NODE_ENV != 'production') {
    console.debug('Abacus tracking datalayer enabled', api, defaultDataLayer);
  }
};

const addAbacusScrollTracking = () => {
  const scrollThresholds = [
    { threshold: 10, event: TrackingEvents.scroll },
    { threshold: 25, event: TrackingEvents.scroll },
    { threshold: 50, event: TrackingEvents.scroll },
    { threshold: 75, event: TrackingEvents.scroll },
    { threshold: 90, event: TrackingEvents.scroll }
  ];

  /**
   * Initialisation
   */
  const initialCurrentPos = window.scrollY;
  const initialInnerHeight = window.innerHeight;
  const initialScrollHeight = getScrollHeight();
  const initialScrollDistance = initialScrollHeight - initialInnerHeight;
  const initialScrollPercentage = initialCurrentPos / (initialScrollDistance / 100);
  scrollThresholds.forEach((t) => {
    if (initialScrollPercentage > 0 && t.threshold <= initialScrollPercentage) {
      scrollCollection[t.threshold] = true;
    }
  });

  /**
   * Pagescroll
   */
  window.addEventListener(TrackingEvents.scroll, function () {
    const currentPos = window.scrollY;
    const innerHeight = window.innerHeight;
    const scrollHeight = getScrollHeight();
    const scrollDistance = scrollHeight - innerHeight;
    const scrollPercentage = currentPos / (scrollDistance / 100);
    scrollThresholds.forEach((t) => {
      if (!scrollCollection[t.threshold]) {
        if (scrollPercentage > t.threshold) {
          const event = {
            trackAbacus: t.event,
            event_interaction: true,
            event_metric: t.threshold,
            scroll_units: 'percent',
            scroll_direction: 'vertical'
          };
          const specificData = getSpecificPageTrackingData();
          if (!specificData.disableScroll) {
            const trackingEvent = new CustomEvent(TrackingEvents.track, {
              detail: event
            });
            window.dispatchEvent(trackingEvent);
          }
          scrollCollection[t.threshold] = true;
        }
      }
    });
  });

  if (process.env.NODE_ENV != 'production') {
    console.debug('Abacus tracking scrolling enabled');
  }
};

const addAbacusClickTracking = () => {
  /**
   * Click tracking.
   */
  document.addEventListener('click', (event) => {
    if (event.target.dataset.trackAbacus) {
      const data = event.target.dataset;
      delete data.track;
      const trackingEvent = new CustomEvent(TrackingEvents.track, {
        detail: data
      });
      window.dispatchEvent(trackingEvent);
    }
  });
  if (process.env.NODE_ENV != 'production') {
    console.debug('Abacus tracking click enabled');
  }
};

/**
 * Core async tracking listener. Drives BOTH GTM and Abacus:
 *  - GTM dataLayer pushes run independently of Abacus (only need `window.dataLayer`).
 *  - Abacus pushes are gated behind `abacusEnabled` with their own backlog guard.
 *
 * event.detail: {
 *    track?: string,
 *    trackAbacus?: string,
 *    trackId?: string,
 *    trackExternalId?: string,
 *    trackText?: string,
 *    trackUrl?: string,
 *    trackTarget?: string,
 *    trackValue?: any,
 *    trackMeta?: object
 * }
 */
const addTrackingEventListener = ({ abacusEnabled }) => {
  window.addEventListener(TrackingEvents.track, (event) => {
    const w = window;
    const data = event.detail;

    // When Abacus is on, defer until the first pageview has populated lastPageLocation
    // (Abacus needs it, and it feeds the GTM virtual-pageview referrer). Backlogged
    // events are replayed via the queue event, so GTM/Abacus each fire exactly once.
    if (abacusEnabled && !lastPageLocation) {
      w[abacusDataLayerName].dataBacklog.push(event);
      return false;
    }

    /**
     * GTM dataLayer (independent of Abacus).
     */
    if (w.dataLayer && data.track) {
      if (data.track === 'virtual_pageview') {
        // Virtual pageviews get their own dataLayer event; the GA4 page_view tag's
        // trigger regex matches this alongside the route-change event.
        w.dataLayer.push({
          event: 'chilli-virtual-pageview',
          page_location: data.trackUrl || w.location.href,
          page_title: data.trackText || document.title,
          page_referrer: lastPageLocation?.page_url || lastGtmPageLocation || document.referrer
        });
      } else {
        w.dataLayer.push({ ...{ event: TrackingEvents.track }, ...data });
        w.dataLayer.push({
          track: undefined,
          trackAbacus: undefined,
          trackId: undefined,
          trackExternalId: undefined,
          trackText: undefined,
          trackUrl: undefined,
          trackTarget: undefined,
          trackValue: undefined,
          trackMeta: undefined
        }); // Reset datalayer, as only reset on page change.
      }
      if (process.env.NODE_ENV != 'production') {
        console.log('Event added to dataLayer', w.dataLayer);
      }
    }

    /**
     * Abacus (only when enabled).
     */
    if (abacusEnabled && data.trackAbacus) {
      const specificData = getSpecificPageTrackingData();
      const trackingData = getTrackingData(data);
      const trackingUserId = w.__chilliTrackingUserId;
      const abacusEvent = {
        event: data.trackAbacus,
        page: withClientSessionOnPage(lastPageLocation),
        data: trackingData.data
      };
      if (trackingUserId) {
        abacusEvent.data = { ...abacusEvent.data, userId: trackingUserId };
      } else if (abacusEvent.data.userId !== undefined) {
        delete abacusEvent.data.userId;
      }
      Object.keys(specificData).length !== 0 && (abacusEvent.data = { ...abacusEvent.data, ...specificData });
      w[abacusDataLayerName].push(abacusEvent);
    }
  });
  if (process.env.NODE_ENV != 'production') {
    console.debug('Tracking event listener enabled');
  }
};

const addAbacusEventQueue = () => {
  /**
   * Backlog, if we dont have the data needed to call an event, we wait till this event called before calling events.
   */
  window.addEventListener(TrackingEvents.queue, function () {
    if (window[abacusDataLayerName].dataBacklog && window[abacusDataLayerName].dataBacklog.length > 0) {
      for (let i = 0; i < window[abacusDataLayerName].dataBacklog.length; i++) {
        const trackingEvent = new CustomEvent(TrackingEvents.track, {
          detail: window[abacusDataLayerName].dataBacklog[i].detail
        });
        window.dispatchEvent(trackingEvent);
      }
    }
  });
};

const addAttributionTracking = () => {
  window._attributionTrackingCookiePrefix = 'attribution_';
  window._addAttributionTrackerCookie = (tracker) => {
    const date = new Date();
    date.setTime(date.getTime() + tracker.cookieExpiryDays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();
    const cookieName = window._attributionTrackingCookiePrefix + tracker.key;
    const cookieValue = JSON.stringify(tracker);
    document.cookie = cookieName + '=' + cookieValue + ';' + expires + ';path=/;SameSite=Lax';
  };

  /**
   * Triggered by: Accept all, Panel -> Deny all, Panel -> Accept all, (limited situations) Panel -> Save settings
   */
  window.addEventListener('cf_consent', function (event) {
    if (window.CookieFirst && window._store?.trackings) {
      const categories = Object.keys(event.detail)
        .map((key) => ({ category: key, value: event.detail[key] }))
        .filter((item) => item.category !== 'necessary' && item.value)
        .map((item) => item.category);
      const allowedCategories = Object.values(categories);
      if (allowedCategories.length > 0) {
        for (const [key, value] of Object.entries(window._store?.trackings)) {
          if (value.cookieConsentPolicies.every((item) => allowedCategories.includes(item))) {
            window._addAttributionTrackerCookie(value);
          }
        }
      }
    }
  });
};

// /**
//  *
//  * @param {*} e
//  */
// const loadScript = (e) => {
//   // save user's choice in a local variable
//   const consent = e.detail || {};

//   // check if our service has been accepted
//   if (consent[abacusDataLayerName]) {
//     // Execute any queued events.
//     window[abacusDataLayerName].queueOnly = false;
//     window.dispatchEvent(new CustomEvent(TrackingEvents.queue));
//   }
// };

exports.onRouteUpdate = (routing, { abacusEnabled }) => {
  const initialHistoryState = { key: Date.now() };
  window.chilliNavigationIndex++;

  if (abacusEnabled) {
    /**
     * Pageview
     */
    if (routing.location?.href != routing.prevLocation?.href) {
      scrollCollection = {};
      const key = Date.now();
      routing.prevLocation = lastPageLocation;
      setTimeout(function () {
        lastPageLocation = {
          page_key: routing.location?.state?.key ? key.toString() : initialHistoryState.key,
          page_url: routing.location?.href,
          page_title: document.title || 'Unknown',
          page_tab_id: initialHistoryState.key.toString(),
          session: getClientSessionId(),
          page_referrer_key: routing.prevLocation?.page_key,
          page_referrer: routing.prevLocation?.page_url || document.referrer
        };
        const event = {
          trackAbacus: 'pageview'
        };
        const trackingEvent = new CustomEvent(TrackingEvents.track, {
          detail: event
        });
        window.dispatchEvent(trackingEvent);
      }, 50);
    }
  }

  /**
   * GTM SPA virtual pageview (independent of Abacus).
   * Read the title after a tick because document.title is not updated
   * synchronously when onRouteUpdate fires.
   */
  const currentHref = (routing.location && routing.location.href) || window.location.href;
  const pageReferrer = lastGtmPageLocation || document.referrer;
  lastGtmPageLocation = currentHref;
  window.dataLayer = window.dataLayer || [];
  setTimeout(function () {
    window.dataLayer.push({
      event: 'chilli-ui-route-change',
      page_location: currentHref,
      page_title: document.title || 'Unknown',
      page_referrer: pageReferrer
    });
  }, 50);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
exports.onClientEntry = (_, { api, defaultDataLayer, abacusEnabled }) => {
  // addXtremePush(window, document, 'xtremepush', process.env.XTREMEPUSH_SDK_PATH);

  // Abacus-only setup (data layer + auto scroll/click interaction tracking + backlog queue).
  if (abacusEnabled) {
    if (process.env.NODE_ENV != 'production') {
      console.debug('Abacus tracking enabled');
    }
    addAbacusDataLayerToWindow(api, defaultDataLayer);
    addAbacusScrollTracking();
    addAbacusClickTracking();
    addAbacusEventQueue();

    // Execute the callback function when banner sends consent events
    // window.addEventListener('cf_services_consent', loadScript);
    // window.addEventListener('cf_services_consent_loaded', loadScript);
  }

  /**
   * Core tracking listener — drives GTM (independent of Abacus) and Abacus (when enabled).
   */
  addTrackingEventListener({ abacusEnabled });

  /**
   * Attribution tracking
   */
  addAttributionTracking();
};
